import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import TanstackTable from "@/components/TanstackTable";
import { CountryRegionDomainApi } from "@/services/CountryRegionDomain.api";
import { CountryRegionDomain } from "@/shared/models/CountryRegionDomain";
import { useAppDispatch } from "@/redux/store";
import { setToastError, setToastSuccess } from "@/redux/shareSlice";

const emptyForm = (): CountryRegionDomain => ({
  countryCode: "",
  countryName: "",
  region: "",
  domain: "",
});

const CountryRegionDomainManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const [records, setRecords] = useState<CountryRegionDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CountryRegionDomain | null>(null);
  const [formData, setFormData] = useState<CountryRegionDomain>(emptyForm());

  const columnHelper = createColumnHelper<CountryRegionDomain>();

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CountryRegionDomainApi.getAll();
      setRecords(data);
    } catch (error) {
      dispatch(setToastError("Failed to load country region domains"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleShowModal = useCallback((record?: CountryRegionDomain) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        countryCode: record.countryCode,
        countryName: record.countryName,
        region: record.region,
        domain: record.domain,
      });
    } else {
      setEditingRecord(null);
      setFormData(emptyForm());
    }
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData(emptyForm());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.countryCode.trim() || !formData.countryName.trim()) {
      dispatch(setToastError("Country code and country name are required"));
      return;
    }

    try {
      if (editingRecord) {
        const success = await CountryRegionDomainApi.update({
          id: editingRecord.id,
          countryCode: formData.countryCode.trim(),
          countryName: formData.countryName.trim(),
          region: formData.region.trim(),
          domain: formData.domain.trim()
        });
        if (success) {
          dispatch(setToastSuccess("Country region domain updated successfully"));
        } else {
          throw new Error("Update failed");
        }
      } else {
        const success = await CountryRegionDomainApi.create({
          countryCode: formData.countryCode.trim(),
          countryName: formData.countryName.trim(),
          region: formData.region.trim(),
          domain: formData.domain.trim(),
        });
        if (success) {
          dispatch(setToastSuccess("Country region domain created successfully"));
        } else {
          throw new Error("Create failed");
        }
      }
      handleCloseModal();
      loadRecords();
    } catch (error) {
      dispatch(setToastError(`Failed to ${editingRecord ? "update" : "create"} country region domain`));
      console.error(error);
    }
  };

  const handleDelete = useCallback(
    async (record: CountryRegionDomain) => {
      if (!window.confirm(`Are you sure you want to delete "${record.countryName}" (${record.countryCode})?`)) {
        return;
      }
      try {
        const success = await CountryRegionDomainApi.remove(record.id!);
        if (success) {
          dispatch(setToastSuccess("Country region domain deleted successfully"));
          loadRecords();
        } else {
          throw new Error("Delete failed");
        }
      } catch (error) {
        dispatch(setToastError("Failed to delete country region domain"));
        console.error(error);
      }
    },
    [dispatch, loadRecords]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("countryCode", {
        header: "Country Code",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("countryName", {
        header: "Country Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("region", {
        header: "Region",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("domain", {
        header: "Domain",
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const record = info.row.original;
          return (
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => handleShowModal(record)}>
                <i className="bi bi-pencil me-1"></i>Edit
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => handleDelete(record)}>
                <i className="bi bi-trash me-1"></i>Delete
              </Button>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [columnHelper, handleShowModal, handleDelete]
  );

  return (
    <div className="country-region-domain-management">
      <div className="action-buttons">
        <Button variant="primary" onClick={() => handleShowModal()}>
          <i className="bi bi-plus-circle"></i> Add New
        </Button>
        <Button variant="secondary" onClick={loadRecords} disabled={loading}>
          <i className="bi bi-arrow-clockwise"></i> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-4">
          <p>No records found. Click "Add New" to create one.</p>
        </div>
      ) : (
        <div className="country-region-domain-table">
          <TanstackTable data={records} columns={columns} paging={true} />
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRecord ? "Edit Country Region Domain" : "Add Country Region Domain"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Country Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. AU"
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Country Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Australia"
                value={formData.countryName}
                onChange={(e) => setFormData({ ...formData, countryName: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Region <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. APAC"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Domain <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. au"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingRecord ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default CountryRegionDomainManagement;
